/**
 * Unified API service layer for Firebase callable functions
 * Provides centralized error handling, loading states, and type safety
 */
import { createCallable } from './firebase';
import { GLAccount } from '../types/glAccount';
import { Currency, Transaction, AccountBalancesAggregatedResponse, TransferPair, TransferStatus, TransferStats, GetEnrichedTransferPairsRequest, GetEnrichedTransferPairsResponse, CreateManualTransferRequest, CreateManualTransferResponse } from '../types';
import { TransactionRule } from '../types/transactionRule';
import {
  CreateLinkTokenRequest,
  CreateLinkTokenResponse,
  ExchangePublicTokenRequest,
  GetConnectionsRequest,
  GetConnectionsResponse,
  SyncTransactionsRequest,
  SyncTransactionsResponse,
  GetAccountBalancesRequest,
  GetAccountBalancesResponse,
  DisconnectAccountRequest,
  DisconnectAccountResponse,
  GetConnectionStatusRequest,
  GetConnectionStatusResponse,
  BulkSyncTransactionsRequest,
  BulkSyncTransactionsResponse,
  TestConnectionRequest,
  TestConnectionResponse,
  GetAnalyticsRequest,
  GetAnalyticsResponse
} from '../types/plaid';
import {
  MonoConnection,
  ExchangeMonoTokenRequest,
  ExchangeMonoTokenResponse,
  SyncMonoTransactionsRequest,
  BulkSyncMonoTransactionsResponse,
  DisconnectMonoRequest,
  DisconnectMonoResponse
} from '../types/mono';
import {
  OnboardingStatusResponse,
  CreateOrganizationInput,
  CreateOrganizationResponse,
  JoinOrganizationInput,
  JoinOrganizationResponse,
  Organization,
  CreateInviteCodeInput,
  CreateInviteCodeResponse
} from '../types/organization';

// API response wrapper type
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
}

// API error class for better error handling
export class ApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Generic API call wrapper with error handling
async function apiCall<T, R>(
  functionName: string,
  data?: T
): Promise<R> {
  try {
    const callable = createCallable<T, R>(functionName);
    const result = await callable(data);
    
    // Firebase callable functions return the data directly
    return result.data;
  } catch (error: any) {
    // Handle Firebase function errors
    if (error.code) {
      throw new ApiError(
        error.message || `Firebase error: ${error.code}`,
        error.code,
        error
      );
    }
    
    // Network or other errors
    throw new ApiError(
      error.message || 'Network error occurred',
      'NETWORK_ERROR',
      error
    );
  }
}

// GL Account API functions
export const glAccountApi = {
  /**
   * Get all GL accounts for the current organization
   */
  async getAll(): Promise<GLAccount[]> {
    return apiCall<void, GLAccount[]>('getGLAccounts');
  },

  /**
   * Create a new GL account
   */
  async create(account: Omit<GLAccount, 'id' | 'active' | 'createdAt' | 'updatedAt'>): Promise<GLAccount> {
    return apiCall<typeof account, GLAccount>('createGLAccount', account);
  },

  /**
   * Update an existing GL account
   */
  async update(id: string, account: Partial<Omit<GLAccount, 'id' | 'createdAt'>>): Promise<GLAccount> {
    return apiCall<{ id: string } & typeof account, GLAccount>('updateGLAccount', { id, ...account });
  },

  /**
   * Delete a GL account
   */
  async delete(id: string): Promise<void> {
    return apiCall<{ id: string }, void>('deleteGLAccount', { id });
  },

  /**
   * Bulk delete GL accounts
   */
  async deleteBulk(ids: string[]): Promise<void> {
    return apiCall<{ ids: string[] }, void>('bulkDeleteGLAccounts', { ids });
  },

  /**
   * Bulk create GL accounts
   */
  async createBulk(accounts: Omit<GLAccount, 'id' | 'active' | 'createdAt' | 'updatedAt'>[]): Promise<any> {
    return apiCall<{ accounts: typeof accounts }, any>('bulkCreateGLAccounts', { accounts });
  }
};

// Currency API functions
export const currencyApi = {
  /**
   * Get all available currencies
   */
  async getAll(): Promise<Currency[]> {
    return apiCall<void, Currency[]>('getCurrencies');
  }
};

// Transaction Rule API functions
export const transactionRuleApi = {
  /**
   * Get all transaction rules for the current organization
   */
  async getAll(filters?: {
    isActive?: boolean;
    glAccountNumber?: string;
    minUseCount?: number;
    maxUseCount?: number;
    searchTerm?: string;
    limit?: number;
    offset?: number;
  }): Promise<TransactionRule[]> {
    const response = await apiCall<typeof filters, { rules: TransactionRule[], totalCount: number, hasMore: boolean }>('getTransactionRules', filters);
    return response.rules || [];
  },

  /**
   * Get active transaction rules only
   */
  async getActive(): Promise<TransactionRule[]> {
    const response = await apiCall<void, { rules: TransactionRule[], totalCount: number, hasMore: boolean }>('getActiveTransactionRules');
    return response.rules || [];
  },

  /**
   * Get a specific transaction rule by ID
   */
  async getById(id: string): Promise<TransactionRule> {
    return apiCall<{ id: string }, TransactionRule>('getTransactionRule', { id });
  },

  /**
   * Create a new transaction rule
   */
  async create(rule: {
    description: string;
    naturalLanguageRule: string;
    glAccountNumber: string;
    glAccountName: string;
    priority?: number;
    createdFromTransactionId?: string;
  }): Promise<TransactionRule> {
    return apiCall<typeof rule, TransactionRule>('createTransactionRule', rule);
  },

  /**
   * Update an existing transaction rule
   */
  async update(data: {
    id: string;
    description?: string;
    naturalLanguageRule?: string;
    glAccountNumber?: string;
    glAccountName?: string;
    priority?: number;
    isActive?: boolean;
  }): Promise<TransactionRule> {
    return apiCall<typeof data, TransactionRule>('updateTransactionRule', data);
  },

  /**
   * Delete a transaction rule
   */
  async delete(id: string): Promise<void> {
    return apiCall<{ id: string }, void>('deleteTransactionRule', { id });
  },

  /**
   * Bulk update rule priorities
   */
  async bulkUpdatePriorities(rules: Array<{ id: string; priority: number }>): Promise<void> {
    return apiCall<{ rules: typeof rules }, void>('bulkUpdateRulePriorities', { rules });
  },

  /**
   * Find matching rule for a transaction
   */
  async matchRule(data: {
    transactionDescription: string;
    transactionAmount: number;
    transactionType: 'income' | 'expense';
  }): Promise<{
    matchedRule?: TransactionRule;
    confidence: number;
    matchingMethod: 'exact' | 'keyword' | 'amount' | 'ai' | 'priority' | 'no_match';
    reasoning?: string;
  }> {
    return apiCall<typeof data, any>('matchTransactionRule', data);
  },

  /**
   * Bulk create transaction rules
   */
  async createBulk(rules: Array<{
    description: string;
    naturalLanguageRule: string;
    glAccountNumber: string;
    glAccountName: string;
    priority?: number;
    createdFromTransactionId?: string;
  }>): Promise<{
    created: TransactionRule[];
    skipped: Array<{ rule: any; reason: string }>;
    errors: Array<{ rule: any; error: string }>;
  }> {
    return apiCall<{ rules: typeof rules }, any>('bulkCreateTransactionRules', { rules });
  },

  /**
   * Bulk delete transaction rules
   */
  async deleteBulk(ids: string[]): Promise<{
    deleted: string[];
    errors: Array<{ id: string; error: string }>;
  }> {
    return apiCall<{ ids: typeof ids }, any>('bulkDeleteTransactionRules', { ids });
  },

  /**
   * Test a transaction rule against sample transactions
   */
  async testRule(data: {
    ruleId: string;
    testTransactions: Array<{
      description: string;
      amount: number;
      type: 'income' | 'expense';
    }>;
  }): Promise<{
    ruleId: string;
    testResults: Array<{
      transaction: any;
      matched: boolean;
      confidence: number;
      reasoning: string;
    }>;
    overallEffectiveness: number;
    suggestions: string[];
  }> {
    return apiCall<typeof data, any>('testTransactionRule', data);
  }
};

// Transaction API functions
export const transactionApi = {
  /**
   * Get all transactions with optional filters and pagination
   */
  async getAll(filters?: any, pagination?: { limit?: number; offset?: number }): Promise<{
    transactions: Transaction[];
    totalCount: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  }> {
    return apiCall<{ filters?: any; pagination?: any }, any>('getTransactions', { filters, pagination });
  },

  /**
   * Get a specific transaction by ID
   */
  async getById(id: string): Promise<Transaction | null> {
    return apiCall<{ id: string }, Transaction | null>('getTransactionById', { id });
  },

  /**
   * Create a new transaction
   */
  async create(transaction: any): Promise<Transaction> {
    // Convert Date to ISO string if needed
    const transactionData = {
      ...transaction,
      timestamp: transaction.timestamp instanceof Date ? transaction.timestamp.toISOString() : transaction.timestamp,
      type: transaction.type === 'income' ? 'INCOME' : 'EXPENSE'
    };
    return apiCall<typeof transactionData, Transaction>('createTransaction', transactionData);
  },

  /**
   * Update an existing transaction
   */
  async update(updateData: any): Promise<Transaction> {
    // Convert Date to ISO string if needed and only include fields that are provided
    const data: any = { ...updateData };
    return apiCall<typeof data, Transaction>('updateTransaction', data);
  },

  /**
   * Delete a transaction
   */
  async delete(id: string): Promise<void> {
    await apiCall<{ id: string }, { success: boolean }>('deleteTransaction', { id });
  },

  /**
   * Bulk import transactions
   */
  async bulkImport(importRequest: any): Promise<any> {
    // Convert dates and types for all transactions
    const request = {
      ...importRequest,
      isCSVImport: true, // Flag for CSV imports
      transactions: importRequest.transactions.map((t: any) => ({
        ...t,
        timestamp: t.timestamp instanceof Date ? t.timestamp.toISOString() : t.timestamp,
        type: t.type === 'income' ? 'INCOME' : 'EXPENSE'
      }))
    };
    return apiCall<typeof request, any>('bulkImportTransactions', request);
  },

  /**
   * Bulk delete transactions
   */
  async bulkDelete(ids: string[]): Promise<any> {
    return apiCall<{ transactionIds: string[] }, any>('bulkDeleteTransactions', { transactionIds: ids });
  },


  /**
   * Predict GL account for a single transaction
   */
  async predictGLAccount(predictionData: any): Promise<any> {
    const data = {
      ...predictionData,
      type: predictionData.type === 'income' ? 'INCOME' : 'EXPENSE'
    };
    console.log("🔮 [API] Calling Firebase function 'predictTransactionGLAccount' with data:", data);
    
    try {
      const result = await apiCall<typeof data, any>('predictTransactionGLAccount', data);
      console.log("🔮 [API] Firebase function 'predictTransactionGLAccount' response:", result);
      return result;
    } catch (error) {
      console.error("🔮 [API] Firebase function 'predictTransactionGLAccount' error:", error);
      throw error;
    }
  },

  /**
   * Batch predict GL accounts for multiple transactions
   */
  async batchPredictGLAccounts(batchRequest: any): Promise<any> {
    const request = {
      transactions: batchRequest.transactions.map((t: any) => ({
        ...t,
        type: t.type === 'income' ? 'INCOME' : 'EXPENSE'
      }))
    };
    console.log("🔮 [API] Calling Firebase function 'batchPredictGLAccounts' with request:", request);
    
    try {
      const result = await apiCall<typeof request, any>('batchPredictGLAccounts', request);
      console.log("🔮 [API] Firebase function 'batchPredictGLAccounts' response:", result);
      return result;
    } catch (error) {
      console.error("🔮 [API] Firebase function 'batchPredictGLAccounts' error:", error);
      throw error;
    }
  },

  /**
   * Update transaction status
   */
  async updateStatus(statusData: any): Promise<Transaction> {
    const data = {
      ...statusData,
      reviewedAt: statusData.reviewedAt instanceof Date ? statusData.reviewedAt.toISOString() : statusData.reviewedAt
    };
    return apiCall<typeof data, Transaction>('updateTransactionStatus', data);
  },

  /**
   * Bulk update transaction status
   */
  async bulkUpdateStatus(bulkStatusData: any): Promise<Transaction[]> {
    const data = {
      ...bulkStatusData,
      reviewedAt: bulkStatusData.reviewedAt instanceof Date ? bulkStatusData.reviewedAt.toISOString() : bulkStatusData.reviewedAt
    };
    return apiCall<typeof data, Transaction[]>('bulkUpdateTransactionStatus', data);
  },

  /**
   * Approve a transaction
   */
  async approveTransaction(transactionId: string, userId?: string): Promise<Transaction> {
    return apiCall<{ transactionId: string; userId?: string }, Transaction>(
      'approveTransaction',
      { transactionId, userId }
    );
  },

  /**
   * Decline a transaction
   */
  async declineTransaction(transactionId: string, userId?: string, reason?: string): Promise<Transaction> {
    return apiCall<{ transactionId: string; userId?: string; reason?: string }, Transaction>(
      'declineTransaction',
      { transactionId, userId, reason }
    );
  },

  /**
   * Bulk approve transactions
   */
  async bulkApproveTransactions(transactionIds: string[]): Promise<{ approvedIds: string[]; failedApprovals: any[]; successCount: number; failureCount: number }> {
    return apiCall<{ transactionIds: string[] }, { approvedIds: string[]; failedApprovals: any[]; successCount: number; failureCount: number }>(
      'bulkApproveTransactions',
      { transactionIds }
    );
  },

  /**
   * Bulk decline transactions
   */
  async bulkDeclineTransactions(transactionIds: string[], reason?: string): Promise<{ declinedIds: string[]; failedDeclines: any[]; successCount: number; failureCount: number }> {
    return apiCall<{ transactionIds: string[]; reason?: string }, { declinedIds: string[]; failedDeclines: any[]; successCount: number; failureCount: number }>(
      'bulkDeclineTransactions',
      { transactionIds, reason }
    );
  },

  /**
   * Bulk assign GL account to transactions
   */
  async bulkAssignGLAccount(transactionIds: string[], glAccountNumber: string, glAccountName: string, glSubcategory?: string, autoApprove?: boolean): Promise<{ updatedIds: string[]; failedUpdates: any[]; successCount: number; failureCount: number }> {
    return apiCall<{ transactionIds: string[]; glAccountNumber: string; glAccountName: string; glSubcategory?: string; autoApprove?: boolean }, { updatedIds: string[]; failedUpdates: any[]; successCount: number; failureCount: number }>(
      'bulkAssignGLAccount',
      { transactionIds, glAccountNumber, glAccountName, glSubcategory, autoApprove }
    );
  },

  /**
   * Update transaction status
   */
  async updateTransactionStatus(transactionId: string, status: string, userId?: string): Promise<Transaction> {
    return apiCall<{ transactionId: string; status: string; userId?: string }, Transaction>(
      'updateTransactionStatus',
      { transactionId, status, userId }
    );
  },

  /**
   * Get transactions by status with pagination
   */
  async getTransactionsByStatus(
    status: string, 
    pagination?: { limit?: number; offset?: number }
  ): Promise<{
    transactions: Transaction[];
    totalCount: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  }> {
    const result = await apiCall<{ status: string; pagination?: any }, any>(
      'getTransactionsByStatus',
      { status, pagination }
    );
    
    // The backend returns the full pagination response
    if (result && result.transactions && Array.isArray(result.transactions)) {
      return result;
    }
    
    // Fallback if response is just an array (backwards compatibility)
    if (Array.isArray(result)) {
      return {
        transactions: result,
        totalCount: result.length,
        hasMore: false,
        limit: pagination?.limit || 50,
        offset: pagination?.offset || 0
      };
    }
    
    console.warn('API: getTransactionsByStatus received unexpected response format:', result);
    return {
      transactions: [],
      totalCount: 0,
      hasMore: false,
      limit: pagination?.limit || 50,
      offset: pagination?.offset || 0
    };
  },

  /**
   * Get transaction status counts
   */
  async getTransactionStatusCounts(): Promise<any> {
    return apiCall<void, any>('getTransactionStatusCounts');
  },

  /**
   * Get transaction status counts (legacy alias)
   */
  async getStatusCounts(): Promise<any> {
    return this.getTransactionStatusCounts();
  },

  /**
   * Get dashboard financial data aggregated by time range and currency
   */
  async getDashboardFinancialData(currency: string = 'USD', timeRange: string = 'daily'): Promise<{
    dailyData: { labels: string[]; income: number[]; expenses: number[] };
    monthlyData: { labels: string[]; income: number[]; expenses: number[] };
    totals: { totalIncome: number; totalExpenses: number; netIncome: number };
  }> {
    return apiCall<{ currency: string; timeRange: string }, any>('getDashboardFinancialData', { currency, timeRange });
  },

  /**
   * Get financial runway data - months of runway based on current burn rate
   */
  async getFinancialRunway(currency: string = 'USD'): Promise<{
    months: number;
    burnRate: number;
    totalSavings: number;
    currentBalance: number;
    isProfitable: boolean;
    tooltipText: string;
  }> {
    return apiCall<{ currency: string }, any>('getFinancialRunway', { currency });
  },

  /**
   * Get account balances with optional currency conversion and aggregated totals
   */
  async getAccountBalances(targetCurrency?: string): Promise<AccountBalancesAggregatedResponse> {
    return apiCall<{ targetCurrency?: string }, AccountBalancesAggregatedResponse>(
      'getAccountBalances', 
      targetCurrency ? { targetCurrency } : undefined
    );
  },

  /**
   * Export transactions to CSV format with streaming progress updates
   */
  async exportTransactions(
    exportRequest: {
      startDate: string;
      endDate: string;
      format: 'csv';
    },
    onProgress?: (progress: { message: string; percentage: number }) => void
  ): Promise<{
    success: boolean;
    downloadUrl?: string;
    totalRecords?: number;
    error?: string;
  }> {
    try {
      // Use the standard API call for now since streaming isn't implemented yet
      const result = await apiCall<typeof exportRequest, {
        success: boolean;
        downloadUrl?: string;
        totalRecords?: number;
        error?: string;
      }>('exportTransactions', exportRequest);
      
      return result;
    } catch (error: any) {
      throw new ApiError(
        error.message || 'Export failed',
        error.code || 'EXPORT_ERROR',
        error
      );
    }
  },

  /**
   * Split a transaction into multiple child transactions
   */
  async splitTransaction(request: any): Promise<any> {
    return apiCall<typeof request, any>('splitTransaction', request);
  },

  /**
   * Get child transactions for a parent transaction
   */
  async getChildTransactions(parentId: string): Promise<Transaction[]> {
    return apiCall<{ parentTransactionId: string }, Transaction[]>('getChildTransactions', { parentTransactionId: parentId });
  }
};

// Plaid API functions
export const plaidApi = {
  /**
   * Create a Plaid Link token for account connection
   */
  async createPlaidLinkToken(request?: CreateLinkTokenRequest): Promise<CreateLinkTokenResponse> {
    return apiCall<CreateLinkTokenRequest | undefined, CreateLinkTokenResponse>('createPlaidLinkToken', request);
  },

  /**
   * Exchange Plaid public token for access token and establish connection
   */
  async exchangePlaidPublicToken(request: ExchangePublicTokenRequest): Promise<any> {
    return apiCall<ExchangePublicTokenRequest, any>('exchangePlaidPublicToken', request);
  },

  /**
   * Get all Plaid connections for the current organization
   */
  async getPlaidConnections(request?: GetConnectionsRequest): Promise<GetConnectionsResponse> {
    return apiCall<GetConnectionsRequest | undefined, GetConnectionsResponse>('getPlaidConnections', request);
  },

  /**
   * Sync transactions from Plaid for a specific connection or all connections
   */
  async syncPlaidTransactions(request?: SyncTransactionsRequest): Promise<SyncTransactionsResponse> {
    return apiCall<SyncTransactionsRequest | undefined, SyncTransactionsResponse>('syncPlaidTransactions', request);
  },

  /**
   * Get account balances from Plaid
   */
  async getPlaidAccountBalances(request?: GetAccountBalancesRequest): Promise<GetAccountBalancesResponse> {
    return apiCall<GetAccountBalancesRequest | undefined, GetAccountBalancesResponse>('getPlaidAccountBalances', request);
  },

  /**
   * Disconnect a Plaid account connection
   */
  async disconnectPlaidAccount(request: DisconnectAccountRequest): Promise<DisconnectAccountResponse> {
    return apiCall<DisconnectAccountRequest, DisconnectAccountResponse>('disconnectPlaidAccount', request);
  },

  /**
   * Get the status of a specific Plaid connection
   */
  async getPlaidConnectionStatus(request: GetConnectionStatusRequest): Promise<GetConnectionStatusResponse> {
    return apiCall<GetConnectionStatusRequest, GetConnectionStatusResponse>('getPlaidConnectionStatus', request);
  },

  /**
   * Bulk sync transactions from multiple Plaid connections
   */
  async bulkSyncPlaidTransactions(request?: BulkSyncTransactionsRequest): Promise<BulkSyncTransactionsResponse> {
    return apiCall<BulkSyncTransactionsRequest | undefined, BulkSyncTransactionsResponse>('bulkSyncPlaidTransactions', request);
  },

  /**
   * Test Plaid connection and service availability
   */
  async testPlaidConnection(request?: TestConnectionRequest): Promise<TestConnectionResponse> {
    return apiCall<TestConnectionRequest | undefined, TestConnectionResponse>('testPlaidConnection', request);
  },

  /**
   * Get Plaid analytics and usage statistics
   */
  async getPlaidAnalytics(request?: GetAnalyticsRequest): Promise<GetAnalyticsResponse> {
    return apiCall<GetAnalyticsRequest | undefined, GetAnalyticsResponse>('getPlaidAnalytics', request);
  }
};

// Mono API for bank integration
export const monoApi = {
  /**
   * Exchange Mono authorization code for connection
   */
  async exchangeAuthToken(request: ExchangeMonoTokenRequest): Promise<ExchangeMonoTokenResponse> {
    return apiCall<ExchangeMonoTokenRequest, ExchangeMonoTokenResponse>('exchangeMonoAuthToken', request);
  },

  /**
   * Get all Mono connections
   */
  async getConnections(): Promise<MonoConnection[]> {
    return apiCall<void, MonoConnection[]>('getMonoConnections');
  },

  /**
   * Get specific Mono connection by ID
   */
  async getConnectionById(connectionId: string): Promise<MonoConnection | null> {
    return apiCall<{ connectionId: string }, MonoConnection | null>('getMonoConnectionById', { connectionId });
  },

  /**
   * Delete/disconnect a Mono connection
   */
  async deleteConnection(request: DisconnectMonoRequest): Promise<DisconnectMonoResponse> {
    return apiCall<DisconnectMonoRequest, DisconnectMonoResponse>('deleteMonoConnection', request);
  },

  /**
   * Sync transactions from Mono connections
   */
  async syncTransactions(request: SyncMonoTransactionsRequest): Promise<BulkSyncMonoTransactionsResponse> {
    return apiCall<SyncMonoTransactionsRequest, BulkSyncMonoTransactionsResponse>('syncMonoTransactions', request);
  },

  /**
   * Health check for Mono services
   */
  async healthCheck(): Promise<{ status: string; services: Record<string, any> }> {
    return apiCall<void, { status: string; services: Record<string, any> }>('monoHealthCheck');
  }
};

// Account API functions
export const accountApi = {
  /**
   * Get all accounts for the current organization
   */
  async getAll(filters?: {
    type?: string;
    currency?: string;
    isActive?: boolean;
    institution?: string;
  }): Promise<any[]> {
    return apiCall<typeof filters, any[]>('getAccounts', filters);
  },

  /**
   * Get a specific account by ID
   */
  async getById(id: string): Promise<any | null> {
    return apiCall<{ id: string }, any | null>('getAccountById', { id });
  },

  /**
   * Create a new account
   */
  async create(account: {
    name: string;
    type: string;
    institution: string;
    balance: number;
    currency: string;
    plaidAccountId?: string;
    monoAccountId?: string;
  }): Promise<any> {
    return apiCall<typeof account, any>('createAccount', account);
  },

  /**
   * Update an existing account
   */
  async update(id: string, account: {
    name?: string;
    type?: string;
    institution?: string;
    balance?: number;
    currency?: string;
    isActive?: boolean;
    plaidAccountId?: string;
    monoAccountId?: string;
  }): Promise<any> {
    return apiCall<{ id: string } & typeof account, any>('updateAccount', { id, ...account });
  },

  /**
   * Delete an account
   */
  async delete(id: string): Promise<void> {
    return apiCall<{ id: string }, void>('deleteAccount', { id });
  },

  /**
   * Get account count for the organization
   */
  async getCount(filters?: {
    type?: string;
    currency?: string;
    isActive?: boolean;
    institution?: string;
  }): Promise<number> {
    return apiCall<typeof filters, number>('getAccountCount', filters);
  },

  /**
   * Check if an account exists by name and institution
   */
  async checkExists(name: string, institution: string): Promise<boolean> {
    return apiCall<{ name: string; institution: string }, boolean>('checkAccountExists', { name, institution });
  }
};

// Health check API
export const healthApi = {
  /**
   * Check API health status
   */
  async check(): Promise<{ status: string; timestamp: string }> {
    return apiCall<void, { status: string; timestamp: string }>('healthCheck');
  }
};

/**
 * Transfer API endpoints
 */
export const transferApi = {
  /**
   * Manually trigger transfer detection
   */
  async detectTransfers(): Promise<{ success: boolean; transfersDetected: number; transfers: TransferPair[] }> {
    return apiCall<void, { success: boolean; transfersDetected: number; transfers: TransferPair[] }>('manualTransferDetection');
  },

  /**
   * Approve a transfer pair
   */
  async approveTransfer(transferId: string): Promise<{ success: boolean; transfer: TransferPair }> {
    return apiCall<{ transferId: string }, { success: boolean; transfer: TransferPair }>('approveTransfer', { transferId });
  },

  /**
   * Reject a transfer pair
   */
  async rejectTransfer(transferId: string, reason?: string): Promise<{ success: boolean; transfer: TransferPair }> {
    return apiCall<{ transferId: string; reason?: string }, { success: boolean; transfer: TransferPair }>('rejectTransfer', { transferId, reason });
  },

  /**
   * Get transfer pairs with optional filtering
   */
  async getTransferPairs(options?: { status?: TransferStatus; limit?: number; offset?: number }): Promise<{ success: boolean; transfers: TransferPair[]; totalCount: number }> {
    return apiCall<{ status?: TransferStatus; limit?: number; offset?: number }, { success: boolean; transfers: TransferPair[]; totalCount: number }>('getTransferPairs', options || {});
  },

  /**
   * Get transfer statistics
   */
  async getTransferStats(): Promise<{ success: boolean; stats: TransferStats }> {
    return apiCall<void, { success: boolean; stats: TransferStats }>('getTransferStats');
  },

  /**
   * Reset a rejected transfer back to NO_MATCH status
   */
  async resetTransfer(transferId: string): Promise<{ success: boolean; transfer: TransferPair }> {
    return apiCall<{ transferId: string }, { success: boolean; transfer: TransferPair }>('resetTransfer', { transferId });
  },

  /**
   * Get enriched transfer pairs with full transaction details, conversion data, and statistics
   */
  async getEnrichedTransferPairs(options?: GetEnrichedTransferPairsRequest): Promise<GetEnrichedTransferPairsResponse> {
    return apiCall<GetEnrichedTransferPairsRequest | undefined, GetEnrichedTransferPairsResponse>('getEnrichedTransferPairs', options);
  },

  /**
   * Create a manual transfer pair between two transactions
   */
  async createManualTransfer(expenseTransactionId: string, incomeTransactionId: string): Promise<CreateManualTransferResponse> {
    const request: CreateManualTransferRequest = {
      expenseTransactionId,
      incomeTransactionId
    };
    return apiCall<CreateManualTransferRequest, CreateManualTransferResponse>('createManualTransfer', request);
  }
};

// Organization API functions
export const organizationApi = {
  /**
   * Check onboarding status for the current user
   * Determines if user has an organization and if onboarding is needed
   */
  async checkOnboardingStatus(): Promise<OnboardingStatusResponse> {
    return apiCall<void, OnboardingStatusResponse>('checkOnboardingStatus');
  },

  /**
   * Create a new organization (user becomes owner)
   */
  async createOrganization(input: CreateOrganizationInput): Promise<CreateOrganizationResponse> {
    return apiCall<CreateOrganizationInput, CreateOrganizationResponse>('createOrganization', input);
  },

  /**
   * Join an existing organization using an invite code
   */
  async joinOrganization(input: JoinOrganizationInput): Promise<JoinOrganizationResponse> {
    return apiCall<JoinOrganizationInput, JoinOrganizationResponse>('joinOrganization', input);
  },

  /**
   * Get the current user's organization
   */
  async getMyOrganization(): Promise<{ organization: Organization; role: string } | null> {
    return apiCall<void, { organization: Organization; role: string } | null>('getMyOrganization');
  },

  /**
   * Create an invite code for the current organization
   * Only admins and owners can create invite codes
   */
  async createInviteCode(input: CreateInviteCodeInput = {}): Promise<CreateInviteCodeResponse> {
    return apiCall<CreateInviteCodeInput, CreateInviteCodeResponse>('createInviteCode', input);
  }
};

// Export all API functions
export const api = {
  glAccount: glAccountApi,
  currency: currencyApi,
  transactionRule: transactionRuleApi,
  transaction: transactionApi,
  account: accountApi,
  transfer: transferApi,
  plaid: plaidApi,
  mono: monoApi,
  health: healthApi,
  organization: organizationApi
};

export default api;